import PageContainer from "../components/PageContainer";
import { Helmet } from 'react-helmet-async';

/**
 * Privacy Policy — testo fornito dal cliente TicinoWild SNC.
 * Solo lingua italiana (versione iniziale). Riferito da CookieBanner.jsx
 * e CookieSettings.jsx tramite Link `/privacy-policy`.
 *
 * Sezione 10 (cookie + banner) e sezione 16 (push notifications) sono
 * allineate all'implementazione tecnica: CookieBanner + Consent Mode v2,
 * nessuna notifica push attiva.
 */
const PrivacyPolicy = () => {
    return (
        <PageContainer items="center" px="4">
            <Helmet>
                <title>Privacy Policy - TicinoWild</title>
                <meta name="description" content="Informativa sulla privacy della piattaforma TicinoWild: dati raccolti, finalità, sicurezza, cookie, diritti dell'utente e contatti." />
                <link rel="canonical" href="https://ticinowild.ch/privacy-policy" />
                <meta property="og:title" content="Privacy Policy - TicinoWild" />
                <meta property="og:description" content="Informativa sulla privacy della piattaforma TicinoWild: dati raccolti, finalità, sicurezza, cookie, diritti dell'utente e contatti." />
                <meta property="og:url" content="https://ticinowild.ch/privacy-policy" />
                <meta name="twitter:title" content="Privacy Policy - TicinoWild" />
                <meta name="twitter:description" content="Informativa sulla privacy della piattaforma TicinoWild: dati raccolti, finalità, sicurezza, cookie, diritti dell'utente e contatti." />
            </Helmet>
            <div className="flex flex-col gap-8 max-w-[600px] mt-8 terms">
                <h3 className="font-normal">Privacy Policy — TicinoWild</h3>

                <p>
                    La presente Privacy Policy descrive come TicinoWild SNC raccoglie, utilizza,
                    conserva e protegge i dati personali degli utenti della piattaforma TicinoWild,
                    accessibile tramite sito web e applicazione mobile TicinoWild distribuita per iOS e Android.
                </p>
                <p>
                    Il trattamento dei dati avviene nel rispetto della Legge federale svizzera sulla
                    protezione dei dati (LPD) e, ove applicabile, del Regolamento Generale sulla Protezione
                    dei Dati dell'Unione Europea (GDPR).
                </p>

                <div>
                    <h4 className="text-[#225424]">1. Titolare del trattamento</h4>
                    <p>Titolare del trattamento è:</p>
                    <p>
                        TicinoWild SNC<br />
                        Via Carona 13<br />
                        CH – 6912 Pazzallo<br />
                        UID/IDI: CHE-491.433.783<br />
                        Email privacy: info@ticinowild.ch
                    </p>
                </div>

                <div>
                    <h4 className="text-[#225424]">2. Tipologie di dati raccolti</h4>

                    <h5 className="text-[#225424] font-semibold mt-3">2.1 Dati forniti dall'utente</h5>
                    <p>Durante l'utilizzo della piattaforma possono essere raccolti:</p>
                    <ul>
                        <li>nome e cognome</li>
                        <li>indirizzo email</li>
                        <li>password (mai memorizzata in chiaro)</li>
                        <li>numero di telefono</li>
                        <li>data di nascita</li>
                        <li>indirizzo di spedizione</li>
                        <li>dati relativi a prenotazioni e acquisti</li>
                        <li>preferiti e cronologia attività</li>
                        <li>comunicazioni inviate tramite moduli o email</li>
                    </ul>
                    <p>Alla registrazione vengono raccolti:</p>
                    <ul>
                        <li>nome</li>
                        <li>indirizzo email</li>
                        <li>password (mai memorizzata in chiaro)</li>
                    </ul>
                    <p>Successivamente, in caso di acquisti o prenotazioni, possono essere raccolti:</p>
                    <ul>
                        <li>cognome</li>
                        <li>numero di telefono</li>
                        <li>data di nascita</li>
                        <li>indirizzo di spedizione</li>
                    </ul>

                    <h5 className="text-[#225424] font-semibold mt-3">2.2 Dati tecnici</h5>
                    <p>Possono inoltre essere raccolti automaticamente:</p>
                    <ul>
                        <li>indirizzo IP anonimizzato</li>
                        <li>tipo di browser e dispositivo</li>
                        <li>sistema operativo</li>
                        <li>lingua del dispositivo</li>
                        <li>log applicativi e dati tecnici di utilizzo</li>
                        <li>dati statistici relativi all'utilizzo della piattaforma</li>
                    </ul>
                </div>

                <div>
                    <h4 className="text-[#225424]">3. Finalità del trattamento</h4>
                    <p>I dati personali vengono trattati per le seguenti finalità:</p>
                    <p><strong>a) Erogazione dei servizi</strong></p>
                    <ul>
                        <li>creazione e gestione account</li>
                        <li>autenticazione e login</li>
                        <li>prenotazioni attività</li>
                        <li>gestione ordini e acquisti</li>
                        <li>elaborazione pagamenti</li>
                        <li>assistenza utenti</li>
                    </ul>
                    <p><strong>b) Obblighi legali</strong></p>
                    <ul>
                        <li>adempimenti contabili e fiscali</li>
                        <li>gestione obblighi normativi</li>
                    </ul>
                    <p><strong>c) Sicurezza e prevenzione abusi</strong></p>
                    <ul>
                        <li>protezione della piattaforma</li>
                        <li>prevenzione accessi non autorizzati</li>
                        <li>gestione log di sicurezza</li>
                        <li>prevenzione frodi</li>
                    </ul>
                    <p><strong>d) Analisi e miglioramento del servizio</strong></p>
                    <ul>
                        <li>analisi statistiche aggregate</li>
                        <li>miglioramento funzionalità e performance</li>
                        <li>monitoraggio tecnico della piattaforma</li>
                    </ul>
                </div>

                <div>
                    <h4 className="text-[#225424]">4. Base giuridica del trattamento</h4>
                    <p>Il trattamento avviene esclusivamente quando sussiste una base giuridica valida, in particolare:</p>
                    <ul>
                        <li>esecuzione del contratto o del servizio richiesto dall'utente</li>
                        <li>adempimento di obblighi legali</li>
                        <li>legittimo interesse del titolare</li>
                        <li>eventuale consenso dell'utente, ove richiesto</li>
                    </ul>
                </div>

                <div>
                    <h4 className="text-[#225424]">5. Sicurezza dei dati</h4>
                    <p>TicinoWild adotta misure tecniche e organizzative adeguate per proteggere i dati personali. In particolare:</p>
                    <ul>
                        <li>comunicazioni protette tramite HTTPS/TLS</li>
                        <li>password salvate con algoritmo bcrypt e salt</li>
                        <li>email archiviate in forma cifrata AES-256 e indicizzate tramite hash SHA-256 per la ricerca</li>
                        <li>token di autenticazione firmati lato server</li>
                        <li>sistemi di rate limiting contro accessi abusivi</li>
                        <li>sanitizzazione degli input e protezione contro attacchi comuni</li>
                        <li>log applicativi con IP anonimizzato (ultimo ottetto IPv4 / ultimi segmenti IPv6 azzerati) e privi di dati personali in chiaro</li>
                    </ul>
                </div>

                <div>
                    <h4 className="text-[#225424]">6. Autenticazione e archiviazione locale</h4>
                    <p>
                        L'autenticazione della piattaforma avviene tramite token JWT firmati lato server,
                        memorizzati localmente sul dispositivo dell'utente.
                    </p>
                    <p>La piattaforma utilizza il localStorage del browser (e della WebView nelle app) per memorizzare:</p>
                    <ul>
                        <li>Token di autenticazione (JWT contenente solo identificativo utente e livello, validità 7 giorni)</li>
                        <li>Preferenze lingua</li>
                        <li>Carrello (lista prodotti selezionati)</li>
                        <li>Riferimenti temporanei ai checkout in corso (cancellati al completamento)</li>
                        <li>Flag tecnici dell'interfaccia (es. stato modale PWA)</li>
                        <li>Copia locale di contenuti pubblici della home (slide del carosello) per velocizzarne l'apertura</li>
                    </ul>
                    <p>Tali dati non contengono password o email in chiaro.</p>
                    <p>
                        L'autenticazione della piattaforma non avviene tramite cookie HTTP di sessione
                        proprietari, ma tramite token JWT memorizzati localmente sul dispositivo dell'utente.
                    </p>
                </div>

                <div>
                    <h4 className="text-[#225424]">7. Geolocalizzazione</h4>
                    <p>
                        L'app può richiedere il permesso di accedere alla posizione del dispositivo
                        esclusivamente per migliorare l'esperienza di navigazione della mappa.
                    </p>
                    <p>Le coordinate geografiche:</p>
                    <ul>
                        <li>restano sul dispositivo dell'utente</li>
                        <li>non vengono salvate sui server TicinoWild</li>
                        <li>non vengono condivise con terze parti</li>
                    </ul>
                    <p>
                        La geolocalizzazione viene utilizzata esclusivamente previa autorizzazione esplicita
                        dell'utente. L'utente può negare il permesso senza compromettere le funzionalità
                        principali della piattaforma.
                    </p>
                </div>

                <div>
                    <h4 className="text-[#225424]">8. Pagamenti</h4>
                    <p>I pagamenti sono gestiti tramite Stripe.</p>
                    <p>
                        TicinoWild non ha accesso diretto ai dati completi delle carte di pagamento, che
                        vengono trattati esclusivamente da Stripe tramite infrastrutture conformi agli
                        standard PCI-DSS.
                    </p>
                    <p>Dati trasmessi a Stripe possono includere:</p>
                    <ul>
                        <li>indirizzo email</li>
                        <li>importo e valuta</li>
                        <li>identificativi tecnici interni relativi agli ordini e alle prenotazioni</li>
                    </ul>
                    <p>
                        Per maggiori informazioni:{' '}
                        <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="underline text-[#225424]">
                            https://stripe.com/privacy
                        </a>
                    </p>
                </div>

                <div>
                    <h4 className="text-[#225424]">9. Servizi e fornitori terzi</h4>
                    <p>
                        Per l'erogazione dei servizi, TicinoWild utilizza fornitori terzi che possono
                        trattare dati personali per conto del titolare.
                    </p>
                    <ul>
                        <li><strong>Stripe</strong> — Gestione pagamenti e Stripe Connect.</li>
                        <li><strong>Cloudinary</strong> — Hosting delle immagini caricate da partner e amministratori.</li>
                        <li><strong>MapTiler</strong> — Mappe interattive e servizi cartografici.</li>
                        <li><strong>Google Analytics 4</strong> — Analisi statistica dell'utilizzo della piattaforma.</li>
                        <li><strong>Hostpoint</strong> — Invio email transazionali.</li>
                        <li><strong>Brevo</strong> — Canale alternativo per email transazionali.</li>
                        <li><strong>Render, Netlify e MongoDB Atlas</strong> — Hosting infrastruttura backend, frontend e database.</li>
                    </ul>
                    <p>
                        Alcuni fornitori possono trattare dati al di fuori della Svizzera o dello Spazio
                        Economico Europeo. In tali casi TicinoWild adotta adeguate garanzie contrattuali,
                        incluse Standard Contractual Clauses (SCC) ove applicabili.
                    </p>
                </div>

                <div>
                    <h4 className="text-[#225424]">10. Cookie e strumenti di analisi</h4>
                    <p>
                        La piattaforma TicinoWild utilizza cookie e tecnologie simili per garantire il
                        corretto funzionamento del sito e dell'applicazione, migliorare l'esperienza
                        dell'utente, analizzare l'utilizzo della piattaforma e, ove applicabile,
                        personalizzare determinati contenuti.
                    </p>
                    <p>
                        I cookie sono piccoli file di testo memorizzati sul dispositivo dell'utente durante
                        la navigazione.
                    </p>

                    <h5 className="text-[#225424] font-semibold mt-3">10.1 Cookie tecnici</h5>
                    <p>
                        TicinoWild utilizza cookie e strumenti tecnici strettamente necessari al
                        funzionamento della piattaforma, inclusi:
                    </p>
                    <ul>
                        <li>autenticazione e mantenimento della sessione tramite token JWT memorizzati localmente</li>
                        <li>gestione delle preferenze di navigazione</li>
                        <li>sicurezza della piattaforma</li>
                        <li>gestione del carrello e dei processi di checkout</li>
                    </ul>
                    <p>
                        Questi strumenti tecnici possono essere utilizzati senza il consenso preventivo
                        dell'utente in quanto necessari al funzionamento del servizio.
                    </p>

                    <h5 className="text-[#225424] font-semibold mt-3">10.2 Cookie analitici e Google Analytics</h5>
                    <p>
                        TicinoWild utilizza Google Analytics 4, un servizio di analisi fornito da Google
                        LLC, per raccogliere informazioni statistiche aggregate sull'utilizzo della
                        piattaforma.
                    </p>
                    <p>I dati raccolti possono includere:</p>
                    <ul>
                        <li>pagine visitate</li>
                        <li>durata delle sessioni</li>
                        <li>interazioni con la piattaforma</li>
                        <li>dati tecnici del dispositivo e del browser</li>
                        <li>indirizzo IP anonimizzato</li>
                    </ul>
                    <p>
                        Google Analytics può utilizzare cookie e identificatori tecnici per distinguere
                        gli utenti e generare statistiche aggregate sull'utilizzo del servizio.
                    </p>
                    <p>
                        L'attivazione dei cookie analitici e degli strumenti di tracciamento non
                        essenziali avviene esclusivamente previo consenso dell'utente tramite l'apposito
                        cookie banner mostrato al primo accesso.
                    </p>
                    <p>L'utente può in qualsiasi momento:</p>
                    <ul>
                        <li>accettare o rifiutare i cookie non essenziali</li>
                        <li>modificare le proprie preferenze</li>
                        <li>revocare il consenso precedentemente prestato</li>
                    </ul>
                    <p>
                        tramite le impostazioni del banner cookie o del proprio browser.
                    </p>
                    <p>
                        Il rifiuto dei cookie non essenziali non compromette il funzionamento principale
                        della piattaforma, ma alcune funzionalità aggiuntive potrebbero risultare limitate.
                    </p>
                    <p>
                        Per maggiori informazioni sul trattamento dei dati da parte di Google:{' '}
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline text-[#225424]">
                            https://policies.google.com/privacy
                        </a>
                    </p>

                    <h5 className="text-[#225424] font-semibold mt-3">10.3 Google Consent Mode</h5>
                    <p>
                        TicinoWild utilizza Google Consent Mode per gestire il consenso relativo agli
                        strumenti di analisi e tracciamento, nel rispetto delle preferenze espresse
                        dall'utente.
                    </p>
                </div>

                <div>
                    <h4 className="text-[#225424]">11. Comunicazioni email</h4>
                    <p>
                        TicinoWild invia esclusivamente comunicazioni transazionali necessarie al
                        funzionamento del servizio, tra cui:
                    </p>
                    <ul>
                        <li>conferme registrazione</li>
                        <li>conferme prenotazione</li>
                        <li>ricevute e pagamenti</li>
                        <li>notifiche relative all'account</li>
                        <li>conferme cancellazione account</li>
                    </ul>
                    <p>
                        Attualmente TicinoWild non invia newsletter o comunicazioni di marketing
                        automatiche. L'utente può iscriversi autonomamente alla newsletter tramite
                        apposito modulo e riceverà tali comunicazioni solo in caso di iscrizione
                        volontaria e previo consenso esplicito. L'utente può inoltre revocare il proprio
                        consenso e disiscriversi in qualsiasi momento tramite l'apposito link presente in
                        ogni comunicazione email. Per l'invio delle newsletter TicinoWild utilizza il
                        servizio Brevo (Sendinblue SAS).
                    </p>
                </div>

                <div>
                    <h4 className="text-[#225424]">12. Conservazione dei dati</h4>
                    <p>
                        I dati personali vengono conservati solo per il tempo necessario alle finalità per
                        cui sono stati raccolti.
                    </p>
                    <p>Alcuni dati relativi a:</p>
                    <ul>
                        <li>ordini</li>
                        <li>fatture</li>
                        <li>pagamenti</li>
                        <li>prenotazioni</li>
                    </ul>
                    <p>
                        possono essere conservati fino a 10 anni in conformità all'art. 957a del Codice
                        delle Obbligazioni svizzero.
                    </p>
                </div>

                <div>
                    <h4 className="text-[#225424]">13. Cancellazione account</h4>
                    <p>L'utente può richiedere la cancellazione del proprio account in qualsiasi momento.</p>
                    <p>La procedura prevede:</p>
                    <ul>
                        <li>Richiesta di cancellazione (tramite Profilo o modulo pubblico)</li>
                        <li>Invio email con link di conferma (valido 30 minuti)</li>
                        <li>Conferma da parte dell'utente</li>
                        <li>Periodo di attesa di 14 giorni (durante il quale è possibile annullare la richiesta)</li>
                        <li>Anonimizzazione irreversibile dei dati personali (eseguita automaticamente)</li>
                    </ul>
                    <p>Una volta completata la procedura:</p>
                    <ul>
                        <li>l'account non è più accessibile</li>
                        <li>l'utente non può essere identificato</li>
                        <li>i dati non possono essere ripristinati</li>
                        <li>le richieste di prenotazione attive vengono chiuse</li>
                        <li>gli ordini completati e le prenotazioni pagate vengono conservati in forma anonimizzata (con timestamp della cancellazione) per obblighi fiscali</li>
                    </ul>
                </div>

                <div>
                    <h4 className="text-[#225424]">14. Diritti dell'utente</h4>
                    <p>L'utente può esercitare i diritti previsti dalla normativa applicabile, inclusi:</p>
                    <ul>
                        <li>accesso ai dati</li>
                        <li>rettifica</li>
                        <li>cancellazione</li>
                        <li>limitazione del trattamento</li>
                        <li>opposizione</li>
                        <li>portabilità dei dati</li>
                        <li>revoca del consenso</li>
                    </ul>
                    <p>
                        Le richieste possono essere inviate a:{' '}
                        <a href="mailto:info@ticinowild.ch" className="underline text-[#225424]">info@ticinowild.ch</a>
                    </p>
                    <p>L'utente ha inoltre diritto di presentare reclamo all'autorità competente:</p>
                    <ul>
                        <li>Svizzera: Incaricato federale della protezione dei dati e della trasparenza (IFPDT)</li>
                    </ul>
                </div>

                <div>
                    <h4 className="text-[#225424]">15. Minori</h4>
                    <p>La piattaforma non è destinata a minori di 13 anni.</p>
                    <p>
                        TicinoWild non effettua attualmente verifiche automatiche dell'età al momento
                        della registrazione.
                    </p>
                </div>

                <div>
                    <h4 className="text-[#225424]">16. Notifiche push</h4>
                    <p>
                        Attualmente l'app TicinoWild non utilizza notifiche push tramite Apple Push
                        Notification Service (APNS) o Firebase Cloud Messaging (FCM).
                    </p>
                </div>

                <div>
                    <h4 className="text-[#225424]">17. Modifiche alla presente Privacy Policy</h4>
                    <p>
                        TicinoWild si riserva il diritto di aggiornare la presente Privacy Policy in
                        qualsiasi momento.
                    </p>
                    <p>
                        La versione aggiornata sarà pubblicata sul sito web e, ove necessario, notificata
                        agli utenti tramite i canali appropriati.
                    </p>
                </div>

                <p className="text-sm text-neutral-500 mt-4">Ultimo aggiornamento: 29 maggio 2026</p>
            </div>
        </PageContainer>
    );
};

export default PrivacyPolicy;
